/**
 * Read-only catalogue lookups shared by the Blinkit-theme browse screens: the tenant's verticals
 * (``GET /business-categories``) and its category groups (``GET /category-groups``). Each response is cached per
 * tenant for the session, so moving between category screens doesn't refetch or flash empty rails.
 */
import { useEffect, useState } from "react";
import { api } from "./api";
import { useTenant } from "../contexts/TenantContext";

const cache = new Map();   // `${tenantId}|${key}` → Promise<array>

function cached(tenantId, key, load) {
    const k = `${tenantId || "-"}|${key}`;
    if (!cache.has(k)) {
        cache.set(k, load().catch((e) => { cache.delete(k); throw e; }));
    }
    return cache.get(k);
}

/** Test hook — the cache is module state. */
export function _clearCatalogCache() { cache.clear(); }

function useCachedList(key, load) {
    const { tenant } = useTenant();
    const [state, setState] = useState({ key: null, data: null, error: false });
    useEffect(() => {
        if (key == null) return undefined;
        let alive = true;
        cached(tenant?.id, key, load)
            .then((data) => { if (alive) setState({ key, data, error: false }); })
            .catch(() => { if (alive) setState({ key, data: [], error: true }); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenant?.id, key]);
    // Never hand back the previous key's list while the new one loads.
    return state.key === key ? state : { key, data: null, error: false };
}

/** All active verticals (parents and children). ``data`` is null while loading. */
export function useVerticals() {
    return useCachedList("verticals", () => api.get("/business-categories").then(({ data }) => data || []));
}

/**
 * Visible category groups — for one vertical when ``businessCategoryId`` is given (plus the tenant's unpinned
 * groups, which is what the endpoint returns), otherwise all of them. Pass ``undefined`` to skip the request.
 */
export function useCategoryGroups(businessCategoryId, { enabled = true } = {}) {
    const key = !enabled ? null : `groups:${businessCategoryId || "*"}`;
    return useCachedList(key, () => api.get("/category-groups", {
        params: businessCategoryId ? { business_category_id: businessCategoryId } : {},
    }).then(({ data }) => data || []));
}
